type Status = 'Quote sent' | 'Booking confirmed' | 'In transit' | 'Delivered'

const money = (value: number) => `$${Math.round(value).toLocaleString('en-US')}`
const tone: Record<Status, string> = {
  'Quote sent': '#2563eb',
  'Booking confirmed': '#8b5cf6',
  'In transit': '#f59e0b',
  Delivered: '#10b981',
}

export function LaneLedger({
  route,
  equipment,
  sell,
  buy,
}: { route: string; equipment: string; sell: number; buy: number }) {
  const rows: {
    ref: string
    date: string
    source: string
    buy: number
    sell: number
    volume: string
    status: Status
    delivered?: string
  }[] = [
    {
      ref: 'Q-261005-184',
      date: '05 Oct 2026',
      source: 'MSC contract',
      buy,
      sell: Math.round(sell * 1.01),
      volume: `2 × ${equipment}`,
      status: 'Quote sent',
    },
    {
      ref: 'SJK-8042',
      date: '28 Sep 2026',
      source: 'SeaBridge partner',
      buy: Math.round(buy * 0.98),
      sell,
      volume: `1 × ${equipment}`,
      status: 'Delivered',
      delivered: '03 Oct 2026',
    },
    {
      ref: 'SJK-7981',
      date: '19 Aug 2026',
      source: 'MSC contract',
      buy: Math.round(buy * 0.95),
      sell: Math.round(sell * 0.98),
      volume: `1 × ${equipment}`,
      status: 'Delivered',
      delivered: '24 Sep 2026',
    },
    {
      ref: 'SJK-7904',
      date: '02 Aug 2026',
      source: 'Spot rate',
      buy: Math.round(buy * 1.02),
      sell: Math.round(sell * 1.03),
      volume: `3 × ${equipment}`,
      status: 'In transit',
    },
  ]
  return (
    <section className="llg">
      <header>
        <div>
          <span>Lane commercial history</span>
          <h3>Rates used on {route}</h3>
        </div>
        <button type="button">Export lane history</button>
      </header>
      <div className="llg-head">
        <span>Reference / quoted</span>
        <span>Rate used</span>
        <span>Volume</span>
        <span>Buy</span>
        <span>Sell</span>
        <span>Profit</span>
        <span>Status</span>
        <span />
      </div>
      <div className="llg-rows">
        {rows.map((row) => (
          <article key={row.ref}>
            <div>
              <b>{row.ref}</b>
              <small>{row.date}</small>
            </div>
            <div>
              <span>{row.source}</span>
              <small>rate source</small>
            </div>
            <strong>{row.volume}</strong>
            <strong>{money(row.buy)}</strong>
            <strong>{money(row.sell)}</strong>
            <strong className="profit">{money(row.sell - row.buy)}</strong>
            <div className="status">
              <b style={{ color: tone[row.status], background: `${tone[row.status]}14` }}>
                {row.status}
              </b>
              {row.delivered && <small>Delivered {row.delivered}</small>}
            </div>
            <button type="button">Open →</button>
          </article>
        ))}
      </div>
      <footer>
        <span>Standard status flow</span>
        <b>Quote sent</b>
        <i /> <b>Booking confirmed</b>
        <i /> <b>In transit</b>
        <i /> <b>Delivered</b>
      </footer>
      <style>{styles}</style>
    </section>
  )
}

const styles = `
.llg{margin-bottom:12px;border:1px solid var(--border);background:var(--surface-0);border-radius:14px;box-shadow:var(--shadow-card);overflow:hidden}.llg *{box-sizing:border-box}.llg>header{display:flex;justify-content:space-between;align-items:center;padding:13px 15px;border-bottom:1px solid var(--border)}.llg>header span{font-family:var(--mono,ui-monospace,monospace);font-size:8px;letter-spacing:.1em;text-transform:uppercase;color:var(--frd-accent);font-weight:700}.llg>header h3{font-family:var(--font-display,Ranade,Georgia,serif);font-size:16px;margin:2px 0 0}.llg>header button,.llg article>button{border:0;background:none;color:var(--frd-accent);font-size:8.5px;font-weight:700}.llg-head,.llg article{display:grid;grid-template-columns:1.05fr 1.1fr .8fr .62fr .62fr .62fr 1.1fr 42px;gap:8px;align-items:center}.llg-head{padding:7px 13px;background:var(--surface-1);font-family:var(--mono,ui-monospace,monospace);font-size:7px;text-transform:uppercase;color:var(--text-muted)}.llg article{padding:9px 13px;border-top:1px solid var(--border)}.llg article:first-child{border-top:0}.llg article b,.llg article small,.llg article span{display:block}.llg article>div:first-child b,.llg article>strong{font-family:var(--mono,ui-monospace,monospace);font-size:9px}.llg article span{font-size:8.5px}.llg article small{font-size:7.5px;color:var(--text-muted);margin-top:2px}.llg article>strong.profit{color:#047857}.llg .status b{width:max-content;padding:3px 6px;border-radius:10px;font-size:7.5px}.llg>footer{display:flex;align-items:center;gap:7px;padding:9px 13px;border-top:1px solid var(--border);font-size:7.5px;color:var(--text-muted)}.llg>footer span{font-family:var(--mono,ui-monospace,monospace);text-transform:uppercase;margin-right:4px}.llg>footer i{width:14px;height:1px;background:var(--border)}.llg>footer b:last-child{color:#047857}
@media(max-width:760px){.llg-head{display:none}.llg article{grid-template-columns:1fr 1fr 1fr}.llg article>div:nth-child(2){grid-column:2/-1}.llg article>strong{padding-top:4px}.llg article>strong:nth-of-type(1):before{content:'Volume '}.llg article>strong:nth-of-type(2):before{content:'Buy '}.llg article>strong:nth-of-type(3):before{content:'Sell '}.llg article>strong:nth-of-type(4):before{content:'Profit '}.llg article>strong:before{display:block;color:var(--text-muted);font-family:var(--mono,ui-monospace,monospace);font-size:6.5px;margin-bottom:2px}.llg .status{grid-column:1/3}.llg article>button{text-align:right}.llg>footer{flex-wrap:wrap}.llg>footer span{width:100%}}
`
